import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Star,
  Trophy,
  Target,
  Play,
  Lock,
  CheckCircle,
  Users,
  Brain,
  Gamepad2,
  BookMarked,
  Crown,
  Clock,
  BarChart3,
  Coins,
  CheckSquare,
  PiggyBank,
  Calculator,
  CreditCard,
  TrendingUp,
  GraduationCap,
  Sprout,
  TreePine,
  TreeDeciduous,
} from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import Layout from "../components/Layout";
import BottomNavigation from "../components/BottomNavigation";
import {
  FinanceSchoolUser,
  Module,
  Lesson,
  Badge,
  WeeklyChallenge,
  ProgressMap,
} from "../types";

const FinanceSchool: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [activeTab, setActiveTab] = useState<
    "modules" | "progress" | "badges" | "challenges" | "ranking"
  >("modules");
  const [selectedLevel, setSelectedLevel] = useState<
    "iniciante" | "intermediario" | "avancado"
  >("iniciante");
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [showModuleDetail, setShowModuleDetail] = useState(false);
  const navigate = useNavigate();

  // User state in the finance school
  const [schoolUser] = useState<FinanceSchoolUser>({
    id: "1",
    userId: user?.id || "",
    level: "iniciante",
    totalStars: 45,
    totalCoins: 120,
    currentModule: "budget-basics",
    completedModules: ["budget-basics", "saving-fundamentals"],
    badges: ["first-lesson", "budget-master"],
    rank: 15,
    createdAt: new Date(),
    lastActivity: new Date(),
  });

  // Finance school modules
  const [modules] = useState<Module[]>([
    {
      id: "budget-basics",
      title: "Budget Fundamentals",
      description: "Learn the basic concepts of personal budgeting",
      level: "iniciante",
      category: "Budget",
      icon: BarChart3,
      color: "from-blue-500 to-blue-600",
      isCompleted: true,
      starsEarned: 15,
      order: 1,
      lessons: [
        {
          id: "what-is-budget",
          moduleId: "budget-basics",
          title: "What is a budget?",
          type: "theory",
          content:
            "A budget is a financial plan that helps you control your spending...",
          isCompleted: true,
          starsEarned: 5,
          order: 1,
        },
        {
          id: "budget-types",
          moduleId: "budget-basics",
          title: "Types of budgets",
          type: "quiz",
          content: "There are different types of budgets...",
          isCompleted: true,
          starsEarned: 5,
          order: 2,
        },
        {
          id: "budget-challenge",
          moduleId: "budget-basics",
          title: "Challenge: Create your first budget",
          type: "challenge",
          content: "Now it's time to put it into practice!",
          isCompleted: true,
          starsEarned: 5,
          order: 3,
        },
      ],
    },
    {
      id: "saving-fundamentals",
      title: "Saving Fundamentals",
      description: "Discover how to save money efficiently",
      level: "iniciante",
      category: "Savings",
      icon: PiggyBank,
      color: "from-green-500 to-green-600",
      isCompleted: true,
      starsEarned: 12,
      order: 2,
      lessons: [
        {
          id: "why-save",
          moduleId: "saving-fundamentals",
          title: "Why save?",
          type: "theory",
          content: "Saving money is fundamental for your financial security...",
          isCompleted: true,
          starsEarned: 4,
          order: 1,
        },
        {
          id: "saving-methods",
          moduleId: "saving-fundamentals",
          title: "Saving methods",
          type: "quiz",
          content: "There are several ways to save...",
          isCompleted: true,
          starsEarned: 4,
          order: 2,
        },
        {
          id: "saving-game",
          moduleId: "saving-fundamentals",
          title: "Game: Savings Race",
          type: "game",
          content: "Test your knowledge in a fun game!",
          isCompleted: true,
          starsEarned: 4,
          order: 3,
        },
      ],
    },
    {
      id: "interest-basics",
      title: "Simple and Compound Interest",
      description: "Understand how interest works and its impact",
      level: "iniciante",
      category: "Financial Math",
      icon: Calculator,
      color: "from-purple-500 to-purple-600",
      isCompleted: false,
      starsEarned: 0,
      order: 3,
      lessons: [
        {
          id: "simple-interest",
          moduleId: "interest-basics",
          title: "Simple Interest",
          type: "theory",
          content:
            "Simple interest is calculated only on the principal amount...",
          isCompleted: false,
          starsEarned: 0,
          order: 1,
        },
        {
          id: "compound-interest",
          moduleId: "interest-basics",
          title: "Compound Interest",
          type: "theory",
          content:
            "Compound interest is calculated on the principal plus accumulated interest...",
          isCompleted: false,
          starsEarned: 0,
          order: 2,
        },
        {
          id: "interest-calculator",
          moduleId: "interest-basics",
          title: "Interest Calculator",
          type: "game",
          content:
            "Practice calculating interest with our interactive calculator!",
          isCompleted: false,
          starsEarned: 0,
          order: 3,
        },
      ],
    },
    {
      id: "debt-management",
      title: "Debt Management",
      description: "Learn to control and eliminate your debts",
      level: "intermediario",
      category: "Financial Management",
      icon: CreditCard,
      color: "from-red-500 to-red-600",
      isCompleted: false,
      starsEarned: 0,
      order: 4,
      lessons: [
        {
          id: "debt-types",
          moduleId: "debt-management",
          title: "Types of debt",
          type: "theory",
          content: "There are different types of debt...",
          isCompleted: false,
          starsEarned: 0,
          order: 1,
        },
        {
          id: "debt-strategies",
          moduleId: "debt-management",
          title: "Debt payoff strategies",
          type: "quiz",
          content: "Learn the best strategies...",
          isCompleted: false,
          starsEarned: 0,
          order: 2,
        },
        {
          id: "debt-simulator",
          moduleId: "debt-management",
          title: "Payoff Simulator",
          type: "challenge",
          content: "Simulate different payoff scenarios...",
          isCompleted: false,
          starsEarned: 0,
          order: 3,
        },
      ],
    },
    {
      id: "investment-basics",
      title: "Investment Fundamentals",
      description: "Introduction to the world of investments",
      level: "avancado",
      category: "Investments",
      icon: TrendingUp,
      color: "from-yellow-500 to-yellow-600",
      isCompleted: false,
      starsEarned: 0,
      order: 5,
      lessons: [
        {
          id: "investment-types",
          moduleId: "investment-basics",
          title: "Types of investments",
          type: "theory",
          content: "Learn about the main types of investments...",
          isCompleted: false,
          starsEarned: 0,
          order: 1,
        },
        {
          id: "risk-return",
          moduleId: "investment-basics",
          title: "Risk vs Return",
          type: "quiz",
          content: "Understand the relationship between risk and return...",
          isCompleted: false,
          starsEarned: 0,
          order: 2,
        },
        {
          id: "portfolio-simulator",
          moduleId: "investment-basics",
          title: "Portfolio Simulator",
          type: "game",
          content: "Build your investment portfolio!",
          isCompleted: false,
          starsEarned: 0,
          order: 3,
        },
      ],
    },
  ]);

  // Available badges
  const [badges] = useState<Badge[]>([
    {
      id: "first-lesson",
      name: "First Lesson",
      description: "Complete your first lesson",
      icon: GraduationCap,
      color: "from-blue-500 to-blue-600",
      requirement: "Complete 1 lesson",
      isEarned: true,
      earnedAt: new Date(),
    },
    {
      id: "budget-master",
      name: "Budget Master",
      description: "Master the fundamentals of budgeting",
      icon: BarChart3,
      color: "from-green-500 to-green-600",
      requirement: "Complete the budget module",
      isEarned: true,
      earnedAt: new Date(),
    },
    {
      id: "saving-hero",
      name: "Saving Hero",
      description: "Learn to save like a professional",
      icon: PiggyBank,
      color: "from-purple-500 to-purple-600",
      requirement: "Complete the savings module",
      isEarned: false,
    },
    {
      id: "interest-wizard",
      name: "Interest Wizard",
      description: "Master interest calculations",
      icon: Calculator,
      color: "from-yellow-500 to-yellow-600",
      requirement: "Complete the interest module",
      isEarned: false,
    },
    {
      id: "debt-destroyer",
      name: "Debt Destroyer",
      description: "Learn to eliminate debt",
      icon: CreditCard,
      color: "from-red-500 to-red-600",
      requirement: "Complete the debt management module",
      isEarned: false,
    },
    {
      id: "investment-guru",
      name: "Investment Guru",
      description: "Become an investment expert",
      icon: TrendingUp,
      color: "from-indigo-500 to-indigo-600",
      requirement: "Complete the investment module",
      isEarned: false,
    },
  ]);

  // Weekly challenges
  const [weeklyChallenges] = useState<WeeklyChallenge[]>([
    {
      id: "save-5-percent",
      title: "Save 5%",
      description: "Save 5% of your money this week",
      goal: "Save 5% of weekly income",
      reward: 50,
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      isActive: true,
      participants: 1250,
      completedBy: [],
    },
    {
      id: "no-impulse-buying",
      title: "No Impulse Buying",
      description: "Don't make impulse purchases for 7 days",
      goal: "Avoid unnecessary purchases",
      reward: 30,
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      isActive: true,
      participants: 890,
      completedBy: [],
    },
  ]);

  // Mapa de progresso
  const [progressMap] = useState<ProgressMap>({
    modules: modules.map((module) => ({
      moduleId: module.id,
      isUnlocked: module.order <= 3,
      isCompleted: module.isCompleted,
      starsEarned: module.starsEarned,
      lessonsCompleted: module.lessons.filter((l) => l.isCompleted).length,
      totalLessons: module.lessons.length,
    })),
    currentPosition: 2,
    totalModules: modules.length,
    completionPercentage: 40,
  });

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
  }, [user, navigate]);

  const filteredModules = modules.filter(
    (module) => module.level === selectedLevel
  );

  const getLevelIcon = (level: string) => {
    switch (level) {
      case "Starter":
        return Sprout;
      case "Intermediate":
        return TreePine;
      case "Advanced":
        return TreeDeciduous;
      default:
        return BookOpen;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "Starter":
        return "from-green-500 to-green-600";
      case "Intermediate":
        return "from-yellow-500 to-yellow-600";
      case "Advanced":
        return "from-red-500 to-red-600";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  const getLessonIcon = (type: string) => {
    switch (type) {
      case "theory":
        return <BookOpen className="w-4 h-4" />;
      case "quiz":
        return <Brain className="w-4 h-4" />;
      case "challenge":
        return <Target className="w-4 h-4" />;
      case "game":
        return <Gamepad2 className="w-4 h-4" />;
      case "story":
        return <BookMarked className="w-4 h-4" />;
      default:
        return <BookOpen className="w-4 h-4" />;
    }
  };

  const handleStartLesson = (lesson: Lesson) => {
    // Navegar para a lição específica baseada no tipo
    switch (lesson.type) {
      case "quiz":
        navigate(`/quiz/${lesson.moduleId}/${lesson.id}`);
        break;
      case "game":
        navigate(`/game/${lesson.game?.type || "memory"}`);
        break;
      case "challenge":
        navigate(`/challenge/${lesson.challenge?.id || "budget-challenge"}`);
        break;
      case "story":
        navigate(`/story/${lesson.story?.id || "financial-journey"}`);
        break;
      case "theory":
        // Implementar navegação para conteúdo teórico
        console.log("Iniciando teoria:", lesson.title);
        break;
      default:
        console.log("Iniciando lição:", lesson.title);
    }
  };

  const handleStartModule = (module: Module) => {
    setSelectedModule(module);
    setShowModuleDetail(true);
  };

  if (!user) {
    return null;
  }

  return (
    <Layout
      title="Financial School"
      subtitle="Learn finances in an interactive and gamified way"
    >
      {/* Header com estatísticas do usuário */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mb-6"
      >
        <div className="card bg-gradient-to-r from-primary-600/20 to-blue-600/20 border-primary-500/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-blue-500 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🎓</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {user.name}
                </h3>
                <p className="text-gray-400 text-sm">
                  Level{" "}
                  {schoolUser.level.charAt(0).toUpperCase() +
                    schoolUser.level.slice(1)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span className="text-white font-semibold">
                    {schoolUser.totalStars}
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <Coins className="w-6 h-6 text-green-400" />
                  <span className="text-white font-semibold">
                    {schoolUser.totalCoins}
                  </span>
                </div>
              </div>
              <p className="text-gray-400 text-xs mt-1">
                Rank #{schoolUser.rank}
              </p>
            </div>
          </div>

          {/* Barra de progresso geral */}
          <div className="mb-3">
            <div className="flex justify-between text-sm text-gray-400 mb-1">
              <span>Overall Progress</span>
              <span>{progressMap.completionPercentage}%</span>
            </div>
            <div className="w-full bg-dark-700 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressMap.completionPercentage}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="h-2 bg-gradient-to-r from-primary-500 to-blue-500 rounded-full"
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Navegação por abas */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mb-6"
      >
        <div className="flex space-x-1 bg-dark-800 p-1 rounded-lg">
          {[
            { id: "modules", label: "Modules", icon: BookOpen },
            { id: "progress", label: "Progress", icon: BarChart3 },
            { id: "badges", label: "Achievements", icon: Trophy },
            { id: "challenges", label: "Challenges", icon: Target },
            { id: "ranking", label: "Ranking", icon: Crown },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-md transition-colors ${
                activeTab === tab.id
                  ? "bg-primary-600 text-white"
                  : "text-gray-400 hover:text-white hover:bg-dark-700"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Conteúdo das abas */}
      <AnimatePresence mode="wait">
        {activeTab === "modules" && (
          <motion.div
            key="modules"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Filtros de nível */}
            <div className="flex space-x-1 sm:space-x-2 mb-4 sm:mb-6 overflow-x-auto">
              {["Starter", "Intermediate", "Advanced"].map((level) => (
                <button
                  key={level}
                  onClick={() => setSelectedLevel(level as any)}
                  className={`flex-shrink-0 flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg transition-colors text-xs sm:text-sm ${
                    selectedLevel === level
                      ? `bg-gradient-to-r ${getLevelColor(level)} text-white`
                      : "bg-dark-800 text-gray-400 hover:text-white"
                  }`}
                >
                  <span className="text-lg">
                    {React.createElement(getLevelIcon(level), {
                      className: "w-5 h-5",
                    })}
                  </span>
                  <span className="font-medium capitalize">{level}</span>
                </button>
              ))}
            </div>

            {/* Lista de módulos */}
            <div className="space-y-3 sm:space-y-4">
              {filteredModules.map((module) => (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="card hover:bg-dark-700/50 transition-colors cursor-pointer"
                  onClick={() => handleStartModule(module)}
                >
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div
                      className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r ${module.color} rounded-xl flex items-center justify-center text-2xl`}
                    >
                      {React.createElement(module.icon, {
                        className: "w-6 h-6 sm:w-8 sm:h-8 text-white",
                      })}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base sm:text-lg font-semibold text-white">
                          {module.title}
                        </h3>
                        <div className="flex items-center space-x-2">
                          {module.isCompleted ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <Play className="w-5 h-5 text-primary-400" />
                          )}
                        </div>
                      </div>

                      <p className="text-gray-400 text-sm mb-3">
                        {module.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-1">
                            <span className="text-gray-400">Categoria:</span>
                            <span className="text-white font-medium">
                              {module.category}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-400" />
                            <span className="text-white">
                              {module.starsEarned}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-400">
                            {module.lessons.filter((l) => l.isCompleted).length}
                            /{module.lessons.length} lessons
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === "progress" && (
          <motion.div
            key="progress"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Mapa de progresso visual */}
            <div className="card mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
                Journey Map
              </h3>
              <div className="space-y-4">
                {progressMap.modules.map((moduleProgress) => {
                  const module = modules.find(
                    (m) => m.id === moduleProgress.moduleId
                  );
                  if (!module) return null;

                  return (
                    <div
                      key={module.id}
                      className="flex items-center space-x-4"
                    >
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          moduleProgress.isCompleted
                            ? `bg-gradient-to-r ${module.color}`
                            : moduleProgress.isUnlocked
                            ? "bg-dark-700 border-2 border-primary-500"
                            : "bg-dark-800 border-2 border-gray-600"
                        }`}
                      >
                        {moduleProgress.isCompleted ? (
                          <CheckCircle className="w-6 h-6 text-white" />
                        ) : moduleProgress.isUnlocked ? (
                          React.createElement(module.icon, {
                            className: "w-6 h-6 text-white",
                          })
                        ) : (
                          <Lock className="w-6 h-6 text-gray-500" />
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="text-white font-medium">
                            {module.title}
                          </h4>
                          <span className="text-sm text-gray-400">
                            {moduleProgress.lessonsCompleted}/
                            {moduleProgress.totalLessons}
                          </span>
                        </div>
                        <div className="w-full bg-dark-700 rounded-full h-2">
                          <div
                            className="h-2 bg-gradient-to-r from-primary-500 to-blue-500 rounded-full"
                            style={{
                              width: `${
                                (moduleProgress.lessonsCompleted /
                                  moduleProgress.totalLessons) *
                                100
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Estatísticas de progresso */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
              <div className="card text-center">
                <BookOpen className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {modules.length}
                </p>
                <p className="text-sm text-gray-400">Modules</p>
              </div>
              <div className="card text-center">
                <CheckSquare className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {modules.filter((m) => m.isCompleted).length}
                </p>
                <p className="text-sm text-gray-400">Completed</p>
              </div>
              <div className="card text-center">
                <Star className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {schoolUser.totalStars}
                </p>
                <p className="text-sm text-gray-400">Stars</p>
              </div>
              <div className="card text-center">
                <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {schoolUser.badges.length}
                </p>
                <p className="text-sm text-gray-400">Conquistas</p>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "badges" && (
          <motion.div
            key="badges"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {badges.map((badge) => (
                <motion.div
                  key={badge.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`card ${
                    badge.isEarned ? "border-yellow-500/30" : "border-gray-700"
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl ${
                        badge.isEarned
                          ? `bg-gradient-to-r ${badge.color}`
                          : "bg-dark-800 border-2 border-gray-600"
                      }`}
                    >
                      {badge.isEarned ? (
                        React.createElement(badge.icon, {
                          className: "w-8 h-8 text-white",
                        })
                      ) : (
                        <Lock className="w-8 h-8 text-gray-500" />
                      )}
                    </div>

                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">
                        {badge.name}
                      </h3>
                      <p className="text-gray-400 text-sm mb-2">
                        {badge.description}
                      </p>
                      <p className="text-xs text-gray-500">
                        {badge.requirement}
                      </p>
                      {badge.isEarned && badge.earnedAt && (
                        <p className="text-xs text-yellow-400 mt-1">
                          Conquistado em{" "}
                          {badge.earnedAt.toLocaleDateString("pt-BR")}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === "challenges" && (
          <motion.div
            key="challenges"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="space-y-4">
              {weeklyChallenges.map((challenge) => (
                <motion.div
                  key={challenge.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="card border-primary-500/30"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-blue-500 rounded-xl flex items-center justify-center">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {challenge.title}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {challenge.description}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-1 text-yellow-400">
                        <Coins className="w-6 h-6 text-green-400" />
                        <span className="font-semibold">
                          {challenge.reward}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-white font-medium mb-2">Objective:</p>
                    <p className="text-gray-400 text-sm">{challenge.goal}</p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-400">
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4" />
                        <span>{challenge.participants} participants</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>
                          Ends in{" "}
                          {Math.ceil(
                            (challenge.endDate.getTime() - Date.now()) /
                              (1000 * 60 * 60 * 24)
                          )}{" "}
                          days
                        </span>
                      </div>
                    </div>

                    <button className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors">
                      Participate
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === "ranking" && (
          <motion.div
            key="ranking"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="card mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
                Global Ranking
              </h3>
              <div className="space-y-3">
                {[
                  { name: "Ana Silva", stars: 150, rank: 1, level: "avancado" },
                  {
                    name: "Carlos Santos",
                    stars: 145,
                    rank: 2,
                    level: "avancado",
                  },
                  {
                    name: "Maria Oliveira",
                    stars: 140,
                    rank: 3,
                    level: "intermediario",
                  },
                  {
                    name: user.name,
                    stars: schoolUser.totalStars,
                    rank: schoolUser.rank,
                    level: schoolUser.level,
                    isCurrentUser: true,
                  },
                  {
                    name: "João Costa",
                    stars: 40,
                    rank: 16,
                    level: "iniciante",
                  },
                ].map((player) => (
                  <div
                    key={player.name}
                    className={`flex items-center space-x-4 p-3 rounded-lg ${
                      player.isCurrentUser
                        ? "bg-primary-600/20 border border-primary-500/30"
                        : "bg-dark-800/50"
                    }`}
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-primary-500 to-blue-500 text-white font-bold text-sm">
                      {player.rank}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-white font-medium">
                          {player.name}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-400 capitalize">
                            {player.level}
                          </span>
                          <div className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-400" />
                            <span className="text-white font-semibold">
                              {player.stars}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de detalhes do módulo */}
      <AnimatePresence>
        {showModuleDetail && selectedModule && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowModuleDetail(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-dark-800 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-16 h-16 bg-gradient-to-r ${selectedModule.color} rounded-xl flex items-center justify-center text-2xl`}
                  >
                    {React.createElement(selectedModule.icon, {
                      className: "w-8 h-8 text-white",
                    })}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      {selectedModule.title}
                    </h2>
                    <p className="text-gray-400 text-sm">
                      {selectedModule.category} • {selectedModule.level}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModuleDetail(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <p className="text-gray-300 mb-6">{selectedModule.description}</p>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Module Lessons
                </h3>
                {selectedModule.lessons.map((lesson) => (
                  <div
                    key={lesson.id}
                    className="flex items-center space-x-4 p-4 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors cursor-pointer"
                    onClick={() => handleStartLesson(lesson)}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        lesson.isCompleted ? "bg-green-600" : "bg-dark-600"
                      }`}
                    >
                      {lesson.isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : (
                        getLessonIcon(lesson.type)
                      )}
                    </div>

                    <div className="flex-1">
                      <h4 className="text-white font-medium">{lesson.title}</h4>
                      <p className="text-gray-400 text-sm">
                        {lesson.type === "theory" && "Theory"}
                        {lesson.type === "quiz" && "Quiz"}
                        {lesson.type === "challenge" && "Challenge"}
                        {lesson.type === "game" && "Game"}
                        {lesson.type === "story" && "Story"}
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Star className="w-4 h-4 text-yellow-400" />
                      <span className="text-white font-semibold">
                        {lesson.starsEarned}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowModuleDetail(false)}
                  className="flex-1 py-3 bg-dark-700 hover:bg-dark-600 text-white font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowModuleDetail(false);
                    // Implementar início do módulo
                  }}
                  className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                >
                  Start Module
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNavigation />
    </Layout>
  );
};

export default FinanceSchool;
